import { addVariant } from '../lib/cart';

class QuickBuyCard extends HTMLElement {
  constructor() {
    super();
    this.variants = JSON.parse(this.getAttribute('data-variants') || '[]');
    this.selections = { option1: null, option2: null, option3: null };
    this.currentStep = 1;

    // --- CALCULATE ONCE DURING INIT ---
    this.totalSteps = [1, 2, 3].filter(i => 
      this.variants[0]?.[`option${i}_name`]
    ).length;
    
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.preventTouchMove = this.preventTouchMove.bind(this);
    this.handleContentClick = this.handleContentClick.bind(this);
    this.originalContentParent = null;
    this.originalContentNextSibling = null;
  }

  preventTouchMove(e) {
    if (!this.content.contains(e.target)) {
      e.preventDefault();
    }
  }

  lockScroll() {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('touchmove', this.preventTouchMove, { passive: false });
  }

  unlockScroll() {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    document.removeEventListener('touchmove', this.preventTouchMove);
  }

  connectedCallback() {
    this.content = this.querySelector('.quickbuy-content');
    this.openBtn = this.querySelector('.quickbuy-open');
    this.closeBtn = this.querySelector('.quickbuy-close');
    this.backBtn = this.querySelector('.quickbuy-back');
    this.selectionDisplay = this.querySelector('.quickbuy-selection');
    this.btnContainer = this.querySelector('.step-1.flex');
    this.cartBtnWrapper = this.querySelector('.quickbuy-cart-button');
    this.isGiftCard = this.content.querySelector('.gift-quickbuy');
    this.originalContentParent = this.content?.parentNode || null;
    this.originalContentNextSibling = this.content?.nextSibling || null;

    if (this.btnContainer) {
      this.btnContainer.classList.add('flex-wrap', 'gap-0');
    }

    this.initEvents();
    this.renderStep(1);
    
    this.addEventListener('click', (e) => e.stopPropagation());
    this.content?.addEventListener('click', this.handleContentClick);
    document.addEventListener('click', this.handleClickOutside);
  }

  disconnectedCallback() {
    this.restoreContentPosition();
    this.content?.removeEventListener('click', this.handleContentClick);
    document.removeEventListener('click', this.handleClickOutside);
    this.unlockScroll();
  }

  handleContentClick(e) {
    e.stopPropagation();
  }

  moveContentToBody() {
    if (this.content && this.content.parentNode !== document.body) {
      document.body.appendChild(this.content);
    }
  }

  restoreContentPosition() {
    if (!this.content || !this.originalContentParent) return;
    if (this.content.parentNode === this.originalContentParent) return;

    if (this.originalContentNextSibling && this.originalContentNextSibling.parentNode === this.originalContentParent) {
      this.originalContentParent.insertBefore(this.content, this.originalContentNextSibling);
    } else {
      this.originalContentParent.appendChild(this.content);
    }
  }

  initEvents() {
    const killEvent = (e) => { e.preventDefault(); e.stopPropagation(); };
    if (this.openBtn) this.openBtn.addEventListener('click', (e) => { killEvent(e); this.toggleContent(true); });
    if (this.closeBtn) this.closeBtn.addEventListener('click', (e) => { killEvent(e); this.toggleContent(false); });
    if (this.backBtn) this.backBtn.addEventListener('click', (e) => { killEvent(e); this.handleBack(); });
  }

  handleClickOutside(e) {
    if (this.content && this.content.classList.contains('is-open')) {
      this.toggleContent(false);
    }
  }

  toggleContent(show) {
    const screenWidth = window.innerWidth;
    if (show) {
      document.querySelectorAll('quickbuy-card').forEach(card => {
        if (card !== this && typeof card.toggleContent === 'function') {
          card.toggleContent(false);
        }
      });

      this.content.classList.add('is-open');
      this.openBtn.classList.add('is-closed');

      if (screenWidth < 768) {
        this.moveContentToBody();
        const overlay = document.querySelector('.overlay');
        if (overlay) {
          overlay.classList.add('overlay--active');
          overlay.removeAttribute('hidden');
        }
        this.lockScroll();
      }
    } else {
      this.content.classList.remove('is-open');
      this.openBtn.classList.remove('is-closed');
      this.reset();

      if (screenWidth < 768) {
        this.unlockScroll();
        const overlay = document.querySelector('.overlay');
        if (overlay) {
          overlay.classList.remove('overlay--active');
          overlay.setAttribute('hidden', true);
        }
      }

      this.restoreContentPosition();
    }
  }

  renderStep(stepNumber) {
    this.currentStep = stepNumber;
    const optionKey = `option${stepNumber}`;

    if (this.selectionDisplay && this.variants.length > 0) {
      const parts = [];
      for (let i = 1; i < stepNumber; i++) {
        if (this.selections[`option${i}`]) parts.push(this.selections[`option${i}`]);
      }
      const currentLabel = this.variants[0][`${optionKey}_name`];
      if (currentLabel) parts.push(`<strong>${currentLabel}</strong>`);
      
      this.selectionDisplay.innerHTML = parts.join(', ');
    }

    if (this.backBtn) this.backBtn.classList.toggle('hidden', stepNumber === 1);
    if (this.closeBtn) this.closeBtn.classList.toggle('hidden', stepNumber !== 1);

    const availableOptions = [...new Set(
      this.variants
        .filter(v => this.isMatch(v, stepNumber - 1))
        .map(v => v[optionKey])
        .filter(val => val !== null && val !== undefined && val !== "")
    )];

    const count = availableOptions.length;
    let widthClass = 'flex-1';
    if (!this.isGiftCard) {
      if (count === 7 || count === 8) widthClass = 'min-w-[33%] flex-1';
      else if (count === 9 || count === 10) widthClass = 'min-w-[20%] flex-1';
    } else {
      widthClass = 'min-w-[25%] flex-1';
    }

    this.btnContainer.innerHTML = '';
    
    availableOptions.forEach(opt => {
      const isSelected = this.selections[optionKey] === opt;
      const hasStock = this.variants.some(v => 
        this.isMatch(v, stepNumber - 1) && v[optionKey] === opt && v.available === true
      );

      const btn = document.createElement('button');
      btn.type = "button";
      const statusClasses = hasStock 
        ? (isSelected ? "bg-white text-grey-900 border-grey-900 z-10" : "bg-white text-grey-900 border-grey-400 hover:bg-grey-100")
        : "bg-grey-50 text-grey-200 border-grey-400 cursor-not-allowed";

      btn.className = `px-1 py-3 border-[0.5px] border-solid text-center text-sm font-500 leading-none transition-colors ${statusClasses} ${widthClass}`;
      btn.textContent = opt.replace(' Fit', '');

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (btn.classList.contains('cursor-not-allowed')) return;
        this.handleSelection(stepNumber, opt);
      });

      this.btnContainer.appendChild(btn);
    });

    // --- DYNAMIC FINALIZE CHECK USING this.totalSteps ---
    const isLastStep = stepNumber === this.totalSteps;
    const currentSelectionMade = !!this.selections[optionKey];
    const completeVariant = this.variants.find(v => this.isMatch(v, this.totalSteps));

    if (completeVariant && isLastStep && currentSelectionMade) {
      this.finalize(completeVariant, false);
    } else {
      this.cartBtnWrapper?.classList.add('hidden');
    }
  }

  handleSelection(step, value) {
    this.selections[`option${step}`] = value;
    const nextStep = step + 1;
    
    // --- HAS NEXT CHECK USING this.totalSteps ---
    const hasNextOptions = nextStep <= this.totalSteps && this.variants.some(v => 
      this.isMatch(v, step) && v[`option${nextStep}`] !== null && v[`option${nextStep}`] !== ""
    );

    if (hasNextOptions) {
      this.renderStep(nextStep);
    } else {
      this.renderStep(step);
    }
  }

  handleBack() {
    if (this.currentStep > 1) {
      this.selections[`option${this.currentStep}`] = null;
      this.renderStep(this.currentStep - 1);
    }
  }

  isMatch(variant, stepLimit) {
    for (let i = 1; i <= stepLimit; i++) {
      if (variant[`option${i}`] !== this.selections[`option${i}`]) return false;
    }
    return true;
  }

  finalize(variant, shouldRender = true) {
    if (!variant) return;

    if (this.backBtn) this.backBtn.classList.toggle('hidden', this.currentStep === 1);
    if (this.closeBtn) this.closeBtn.classList.toggle('hidden', this.currentStep !== 1);

    const summary = [variant.option1, variant.option2, variant.option3].filter(Boolean).join(', ');
    this.selectionDisplay.innerHTML = summary;

    if (this.cartBtnWrapper) {
      this.cartBtnWrapper.classList.remove('hidden');
      const cartBtn = this.cartBtnWrapper.querySelector('button');
      cartBtn.setAttribute('data-variant-id', variant.id);

      cartBtn.onclick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        cartBtn.disabled = true;
        const icon = cartBtn.querySelector('.icon');
        const loader = cartBtn.querySelector('.load-ring');
        if (icon) icon.classList.add('hidden');
        if (loader) loader.style.display = 'inline-block';

        try {
          await addVariant({
            variantId: variant.id,
            quantity: 1,
            callback: () => this.toggleContent(false)
          });
        } catch (error) {
          console.error(error);
        } finally {
          cartBtn.disabled = false;
          if (icon) icon.classList.remove('hidden');
          if (loader) loader.style.display = 'none';
        }
      };
    }
  }

  reset() {
    this.selections = { option1: null, option2: null, option3: null };
    this.renderStep(1);
  }
}

if (!customElements.get('quickbuy-card')) {
  customElements.define('quickbuy-card', QuickBuyCard);
}
